import React, { useCallback, useEffect, useMemo, useState } from "react";
import OracleLogo from "@/components/OracleLogo";
import ModeTabs from "@/components/ModeTabs";
import QuickPicks from "@/components/QuickPicks";
import OandaPanel from "@/components/OandaPanel";
import OandaFxTradingScreen from "@/components/oanda/OandaFxTradingScreen";
import ChatPanel from "@/components/ChatPanel";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import OracleStatusBadges from "@/components/OracleStatusBadges";
import OracleToast from "@/components/OracleToast";
import ReportCenter from "@/components/ReportCenter";
import AuthPanel from "@/components/AuthPanel";
import AlertsPanel from "@/components/AlertsPanel";
import LockedFeature, { TierBadge } from "@/components/membership/LockedFeature";
import { useMembership } from "@/components/membership/MembershipContext";
import { oracleApi, getAuthToken, setAuthToken } from "@/api/oracleClient";
import { AGENT_MODES, ORACLE_TEST_IDS } from "@/constants/oracle";
import RetirementCalculator from "@/components/RetirementCalculator";
import PlatformReport from "@/components/PlatformReport";
import Logix2Dock from "@/components/Logix2Dock";
import GlobalIntelligenceChyron from "@/components/GlobalIntelligenceChyron";
// Phase 30 — Animated Market Intelligence Experience Pack (additive only)
import MarketPulseHero from "@/components/animation/MarketPulseHero";
import GlobalMarketMap from "@/components/animation/GlobalMarketMap";
import SectorHeatmap from "@/components/animation/SectorHeatmap";
import DailyInsightCards from "@/components/animation/DailyInsightCards";
import ChyronTicker from "@/components/animation/ChyronTicker";
import Logix2VoiceDock from "@/components/animation/Logix2VoiceDock";
import { useAnimationSettings } from "@/components/animation/AnimationSettings";
// Phase 31 — Personalization Engine (additive only)
import OraclePersonalizationEngine from "@/components/personalization/OraclePersonalizationEngine";
import OracleUserProfileBadge from "@/components/personalization/OracleUserProfileBadge";
import UserPreferenceSetup from "@/components/personalization/UserPreferenceSetup";
import PersonalizedChyronTicker from "@/components/personalization/PersonalizedChyronTicker";
import { usePersonalization } from "@/components/personalization/PersonalizationContext";
// Phase 32A — ORACLE Generation-3 Capability Absorption (additive only)
import SignalCommandCenter from "@/components/signal/SignalCommandCenter";
import EventIntelligenceCenter from "@/components/event/EventIntelligenceCenter";
import AgentSwarmConsole from "@/components/swarm/AgentSwarmConsole";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import SportsResponsibleGamingGate from "@/components/sports/SportsResponsibleGamingGate";
import { checkPublicOnly } from "@/utils/healthcareGuardrail";
import OracleGlobalIntelGrid from "@/components/predictor/OracleGlobalIntelGrid";
import MarketPredictorHub from "@/components/predictor/MarketPredictorHub";
// Phase 33 — Predictive Intelligence + Live Feeds (additive home embeds)
import OraclePredictiveSummary from "@/components/predictive/OraclePredictiveSummary";
import OpportunityGraph from "@/components/animated/OpportunityGraph";
import SignalPulse from "@/components/animated/SignalPulse";
import GlobalIntelFeed from "@/components/animated/GlobalIntelFeed";
import GrantDiscoveryFeed from "@/components/animated/GrantDiscoveryFeed";
import StartupRadar from "@/components/animated/StartupRadar";
import HealthcareOpportunityRadar from "@/components/animated/HealthcareOpportunityRadar";
import AgentSwarmActivityPanel from "@/components/swarm/AgentSwarmActivityPanel";

const LS_SESSION = "carelogix_oracle_session_id";
const LS_USER = "carelogix_oracle_user_id";
const REPORT_TAB_ID = "report_center";
const PLATFORM_REPORT_TAB_ID = "platform_report";

function getOrCreateUserId() {
  try {
    const existing = localStorage.getItem(LS_USER);
    if (existing) return existing;
    const id = `user_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(LS_USER, id);
    return id;
  } catch {
    return "default_user";
  }
}

export default function Oracle() {
  // Active tab can be a mode id OR "report_center"
  const [activeTab, setActiveTab] = useState("stocks");
  const [chatByMode, setChatByMode] = useState({});
  const [metadataByMode, setMetadataByMode] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Enterprise state
  const [userId, setUserId] = useState(getOrCreateUserId);
  const [currentUser, setCurrentUser] = useState(null); // {user_id, email, name, role, is_bypass}
  const [authOpen, setAuthOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [currentSessionId, setCurrentSessionId] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem(LS_SESSION)) || null
  );
  const [adminStatus, setAdminStatus] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [toast, setToast] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  // Phase 23 — Public Member Auth hook (cookie + bearer). Separate from Phase 7
  // internal RBAC (AuthPanel).
  const memberAuth = useAuth();
  // Phase 22 — Pro Trader (OandaFxTradingScreen) toggle for OANDA mode
  const [oandaProTrader, setOandaProTrader] = useState(() => {
    try { return localStorage.getItem("carelogix_oanda_pro") === "1"; } catch { return false; }
  });
  const toggleProTrader = useCallback((v) => {
    setOandaProTrader(v);
    try { localStorage.setItem("carelogix_oanda_pro", v ? "1" : "0"); } catch { /* ignore */ }
  }, []);

  const isReportTab = activeTab === REPORT_TAB_ID;
  const isPlatformReportTab = activeTab === PLATFORM_REPORT_TAB_ID;
  const activeMode = (isReportTab || isPlatformReportTab) ? "stocks" : activeTab;

  const activeModeMeta = useMemo(
    () => AGENT_MODES.find((m) => m.id === activeMode) || AGENT_MODES[0],
    [activeMode]
  );
  const messages = useMemo(() => chatByMode[activeMode] || [], [chatByMode, activeMode]);
  const currentMeta = metadataByMode[activeMode] || null;

  const showToast = useCallback((kind, message, title) => {
    setToast({ kind, message, title, duration: kind === "error" ? 5200 : 3600 });
  }, []);

  // ------- bootstrap: live + admin status + watchlist + persisted history
  const refreshAdmin = useCallback(async () => {
    try {
      const [{ data: live }, { data: admin }] = await Promise.all([
        oracleApi.liveStatus(),
        oracleApi.adminStatus(),
      ]);
      setLiveStatus(live);
      setAdminStatus(admin);
    } catch (e) {
      console.error("status fetch failed", e?.message);
    }
  }, []);

  const refreshWatchlist = useCallback(async () => {
    try {
      const { data } = await oracleApi.watchlistList(userId);
      setWatchlist(data?.watchlist || []);
    } catch (e) {
      console.error("watchlist fetch failed", e?.message);
    }
  }, [userId]);

  // Load history for a known session (once)
  const loadHistory = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const { data } = await oracleApi.history(sessionId);
      const byMode = {};
      (data?.messages || []).forEach((m) => {
        if (!byMode[m.mode]) byMode[m.mode] = [];
        byMode[m.mode].push({ role: m.role, content: m.content });
      });
      setChatByMode((prev) => ({ ...byMode, ...prev })); // prefer in-memory if already there
    } catch (e) {
      console.error("history fetch failed", e?.message);
      // session expired/missing — drop it
      if (e?.response?.status === 404) {
        localStorage.removeItem(LS_SESSION);
        setCurrentSessionId(null);
      }
    }
  }, []);

  const refreshAlerts = useCallback(async (uid) => {
    const id = uid || currentUser?.user_id || userId;
    if (!id) return;
    try {
      const { data } = await oracleApi.alertsList(id, true);
      setUnreadAlerts((data?.alerts || []).length);
    } catch {
      setUnreadAlerts(0);
    }
  }, [currentUser, userId]);

  const bootstrapAuth = useCallback(async () => {
    try {
      const { data } = await oracleApi.authMe();
      if (data?.user_id) {
        setCurrentUser(data);
        if (!data.is_bypass) {
          setUserId(data.user_id);
        }
        refreshAlerts(data.user_id);
      }
    } catch {
      // token invalid — drop it and fall back to bypass
      setAuthToken(null);
      setCurrentUser({ user_id: userId, role: "admin", is_bypass: true });
    }
  }, [refreshAlerts, userId]);

  useEffect(() => {
    refreshAdmin();
    refreshWatchlist();
    bootstrapAuth();
    if (currentSessionId) loadHistory(currentSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- chat
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text ?? input).trim();
      if (!trimmed) {
        setError("Please type a question or pick one above.");
        return;
      }
      // Phase 26F — Healthcare MNPI / PHI client mirror (server is source of truth)
      const publicCheck = checkPublicOnly(trimmed);
      if (publicCheck.ok === false) {
        setError(
          `Public-info-only mode: ${publicCheck.message} ` +
          (publicCheck.hits?.length ? `(matched: ${publicCheck.hits.join(", ")})` : "")
        );
        try {
          showToast(
            "warn",
            "Healthcare guardrail blocked your message. Public information only — please rephrase.",
            "MNPI / PHI Lock"
          );
        } catch (_) { /* ignore */ }
        return;
      }
      setError("");
      const newMessages = [...messages, { role: "user", content: trimmed }];
      setChatByMode((s) => ({ ...s, [activeMode]: newMessages }));
      setInput("");
      setLoading(true);
      try {
        const { data } = await oracleApi.chat({
          mode: activeMode,
          messages: newMessages,
          session_id: currentSessionId || undefined,
          user_id: userId,
        });
        const reply = data?.reply || "(No reply returned.)";
        const newSession = data?.session_id;
        if (newSession && newSession !== currentSessionId) {
          setCurrentSessionId(newSession);
          try {
            localStorage.setItem(LS_SESSION, newSession);
          } catch (_) { /* ignore */ }
        }
        setChatByMode((s) => ({
          ...s,
          [activeMode]: [...newMessages, { role: "assistant", content: reply }],
        }));
        setMetadataByMode((s) => ({
          ...s,
          [activeMode]: {
            metadata: data?.metadata || {},
            data_freshness: data?.data_freshness || "public",
            human_review_required: !!data?.human_review_required,
          },
        }));
        // If backend signaled a handled upstream degradation, surface a soft toast
        // (NOT a red error banner — the assistant fallback message already speaks
        //  to the user; this is just a passive operational notice).
        if (data?.error?.handled || data?.metadata?.upstream_status === "degraded") {
          try {
            showToast("warn", "ORACLE reasoning engine returned a degraded response. Please retry shortly.", "Service Notice");
          } catch (_) { /* ignore */ }
        }
        // Refresh alerts (verdict change on a watchlisted symbol may have fired one)
        refreshAlerts();
      } catch (e) {
        // Constitutional Fix Pack §4 — graceful frontend fallback.
        // Never show a raw red 502/network banner. Instead surface a calm
        // assistant message so the user can simply retry.
        const status = e?.response?.status;
        const handled = e?.response?.data?.error?.handled === true;
        const fallbackReply = handled && e?.response?.data?.reply
          ? e.response.data.reply
          : (
              "## ORACLE TEMPORARY SERVICE NOTICE\n" +
              "- The backend is online, but the reasoning engine returned an upstream error.\n" +
              (status ? `- Status: ${status}\n` : "- Status: network\n") +
              "- Please retry after the service reconnects.\n\n" +
              "*This is AI research for educational purposes only — not financial advice.*"
            );
        setChatByMode((s) => ({
          ...s,
          [activeMode]: [...newMessages, { role: "assistant", content: fallbackReply }],
        }));
        setMetadataByMode((s) => ({
          ...s,
          [activeMode]: {
            metadata: {
              risk_level: "UNKNOWN",
              human_review_required: true,
              data_freshness: "unavailable",
              upstream_status: "degraded",
            },
            data_freshness: "unavailable",
            human_review_required: true,
          },
        }));
        // Inline error is left blank to avoid the red banner pattern.
        setError("");
        try {
          showToast("warn", "ORACLE reasoning engine temporarily unavailable. Your message is recorded — please retry.", "Service Notice");
        } catch (_) { /* ignore */ }
      } finally {
        setLoading(false);
      }
    },
    [activeMode, input, messages, currentSessionId, userId, showToast, refreshAlerts]
  );

  const handlePick = useCallback(
    (prompt) => {
      if (loading) return;
      sendMessage(prompt);
    },
    [loading, sendMessage]
  );

  const handleClear = useCallback(async () => {
    // Constitutional: always clickable; clears for the active mode without
    // disturbing the active tab/mode itself.
    setChatByMode((s) => ({ ...s, [activeMode]: [] }));
    setMetadataByMode((s) => ({ ...s, [activeMode]: null }));
    setError("");
    setInput("");
    showToast("success", "ORACLE workspace cleared.", "Cleared");
  }, [activeMode, showToast]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ------- enterprise actions
  const onExportPdf = useCallback(async () => {
    // Constitutional: never silently disable. Surface a friendly toast when
    // no analysis session exists yet.
    if (!currentSessionId || messages.length === 0) {
      showToast("error", "Run an ORACLE analysis before exporting.", "No session yet");
      return;
    }
    setExportLoading(true);
    try {
      const url = oracleApi.exportPdfUrl(currentSessionId, activeMode);
      // Fetch as blob to surface errors cleanly
      const resp = await fetch(url);
      if (!resp.ok) {
        let msg = `Export failed (${resp.status})`;
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `carelogix_oracle_${activeMode}_${currentSessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      showToast("success", "Intelligence Dossier downloaded.", "PDF Export Ready");
    } catch (e) {
      showToast("error", e?.message || "PDF export failed.", "Export Error");
    } finally {
      setExportLoading(false);
    }
  }, [currentSessionId, activeMode, messages.length, showToast]);

  const onAddToWatchlist = useCallback(async () => {
    // Constitutional: never silently disable. If no asset has been detected
    // from the current session, prompt for one (small browser prompt) so the
    // button is always actionable.
    const meta = currentMeta?.metadata;
    let symbol =
      meta?.asset_or_event ||
      (messages.filter((m) => m.role === "user").slice(-1)[0]?.content?.slice(0, 32) ?? "");
    if (!symbol || !String(symbol).trim()) {
      const promptLabel = `What ${activeMode} asset or event should LOGIX-2 watch? (e.g. NVDA, BTC, EUR/USD)`;
      const answer = (typeof window !== "undefined" && window.prompt(promptLabel, "")) || "";
      symbol = answer.trim();
      if (!symbol) {
        showToast("error", "Watchlist entry cancelled.", "Watchlist");
        return;
      }
    }
    try {
      await oracleApi.watchlistAdd({
        user_id: userId,
        mode: activeMode,
        symbol: String(symbol).toUpperCase().slice(0, 32),
        note: meta?.verdict ? `Verdict: ${meta.verdict}` : "",
        metadata: meta || {},
      });
      await refreshWatchlist();
      showToast("success", "Added to Watchlist.", "Watchlist Updated");
    } catch (e) {
      showToast("error", e?.response?.data?.detail || e?.message || "Failed to save.", "Watchlist Error");
    }
  }, [activeMode, currentMeta, messages, userId, refreshWatchlist, showToast]);

  const onClearSession = useCallback(async () => {
    if (!currentSessionId) return;
    try {
      await oracleApi.deleteSession(currentSessionId);
    } catch (_) { /* ignore */ }
    try {
      localStorage.removeItem(LS_SESSION);
    } catch (_) { /* ignore */ }
    setCurrentSessionId(null);
    setChatByMode({});
    setMetadataByMode({});
    showToast("success", "Session cleared.", "Fresh Slate");
  }, [currentSessionId, showToast]);

  const onRefreshFeed = useCallback(async () => {
    await refreshAdmin();
    showToast("success", "Live feed status refreshed.");
  }, [refreshAdmin, showToast]);

  // ------- auth handlers
  const onAuthed = useCallback((user) => {
    setCurrentUser(user);
    if (user?.user_id) setUserId(user.user_id);
    refreshAlerts(user?.user_id);
    refreshWatchlist();
  }, [refreshAlerts, refreshWatchlist]);

  const onSignOut = useCallback(() => {
    setAuthToken(null);
    setCurrentUser({ user_id: userId, role: "admin", is_bypass: true });
    showToast("success", "Signed out. Reverted to default workspace.");
  }, [userId, showToast]);

  // ------- derived status
  const oandaConnected = !!liveStatus?.oanda?.configured;
  const mongoOk = adminStatus?.mongo === "connected";
  const llmOk = adminStatus?.anthropic === "configured";
  const dataFreshness = currentMeta?.data_freshness || (activeMode === "oanda" ? (oandaConnected ? "live" : "simulated") : "public");
  const hitlRequired = !!currentMeta?.human_review_required;

  return (
    <div className="oracle-shell">
      <OracleToast toast={toast} onClose={() => setToast(null)} />
      <AuthPanel
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onAuthed={onAuthed}
        toast={showToast}
      />
      <AlertsPanel
        open={alertsOpen}
        onClose={() => { setAlertsOpen(false); refreshAlerts(); }}
        userId={currentUser?.user_id || userId}
        onChanged={() => refreshAlerts()}
        toast={showToast}
      />

      {/* Sticky Header */}
      <header className="oracle-header">
        <div className="oracle-header__brand">
          <OracleLogo size={48} />
          <div className="oracle-header__titles">
            <div className="oracle-header__h1">
              CareLogix<sup>™</sup> ORACLE
            </div>
            <div className="oracle-header__h2" data-testid="oracle-positioning">
              Multi-Domain Intelligence Command<sup>™</sup>
            </div>
            <div className="oracle-header__h3" data-testid="oracle-engine-tagline">
              Markets · Capital · Grants · Contracts · Research · Health · Recovery · Governance · Executive Briefings
            </div>
          </div>
        </div>
        <div className="oracle-header__meta">
          {/* Phase 23 — Public Member account chip (always rendered; non-interfering) */}
          {memberAuth?.isAuthenticated ? (
            <div className="oracle-member-pill" data-testid="oracle-member-pill" title={memberAuth.user?.email || ""}>
              <span className={`oracle-member-pill__tier oracle-member-pill__tier--${memberAuth.user?.tier || "free"}`}>
                {(memberAuth.user?.tier || "free").replace(/_/g, " ")}
              </span>
              <span className="oracle-member-pill__name">{memberAuth.user?.name || memberAuth.user?.email}</span>
              <RouterLink to="/access" className="oracle-member-pill__manage" data-testid="oracle-member-manage" title="Manage access">⚙</RouterLink>
            </div>
          ) : memberAuth?.isGuest ? (
            <RouterLink
              to="/access"
              className="oracle-btn oracle-btn--ghost oracle-btn--sm"
              data-testid="oracle-member-upgrade"
              title="Guest mode — sign in to unlock LOGIX-2 & Pro features"
            >Guest · Sign In</RouterLink>
          ) : (
            <RouterLink
              to="/access"
              className="oracle-btn oracle-btn--gold oracle-btn--sm"
              data-testid="oracle-member-access"
              title="Open the Access screen"
            >Account</RouterLink>
          )}
          <button
            type="button"
            className="oracle-iconbtn"
            onClick={() => setAlertsOpen(true)}
            data-testid="oracle-alerts-bell"
            title="Watchlist alerts"
            aria-label="Open alerts"
          >
            <span aria-hidden="true">🔔</span>
            {unreadAlerts > 0 && (
              <span className="oracle-iconbtn__dot" data-testid="oracle-alerts-count">{unreadAlerts > 9 ? "9+" : unreadAlerts}</span>
            )}
          </button>
          {currentUser && !currentUser.is_bypass ? (
            <div className="oracle-user-pill" data-testid="oracle-user-pill">
              <span className={`oracle-role oracle-role--${currentUser.role}`}>{currentUser.role}</span>
              <span className="oracle-user-pill__email">{currentUser.email || currentUser.name || "user"}</span>
              <button
                type="button"
                className="oracle-iconbtn oracle-iconbtn--ghost"
                onClick={onSignOut}
                data-testid="oracle-signout"
                title="Sign out"
                aria-label="Sign out"
              >
                ⎋
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="oracle-btn oracle-btn--ghost oracle-btn--sm"
              onClick={() => setAuthOpen(true)}
              data-testid="oracle-signin"
            >
              Sign In
            </button>
          )}
          <span className="oracle-pulse" />
          <span className="oracle-header__status">ORACLE ONLINE · v{adminStatus?.version || "1.2.0"}</span>
          <MembershipHeaderBadge />
        </div>
      </header>

      {/* Status Bar */}
      <OracleStatusBadges
        oracleOnline={true}
        dataFreshness={dataFreshness}
        oandaConnected={oandaConnected}
        humanReviewRequired={hitlRequired}
        mongoConnected={mongoOk}
        llmConfigured={llmOk}
      />

      {/* Phase 26C — Persistent Compliance Lock™ banner (mandated copy) */}
      <ComplianceLock variant="banner" policy="v27" testid="oracle-compliance-lock-top" />

      {/* Phase 30E — Animated ORACLE Market Pulse Hero (greets authenticated user; preserves existing layout) */}
      <div style={{ padding: "12px var(--oracle-pad, 24px) 0", minWidth: 0 }} data-testid="oracle-phase30-hero-wrap">
        <MarketPulseHero testid="oracle-market-pulse-hero" />
      </div>

      {/* Phase 31 — Personalization companion (badge + preferences modal trigger) */}
      <Phase31BadgeAndPrefs />

      {/* Phase 27 — Global Intelligence Grid + Market Predictor Hub (additive, above existing content) */}
      <div style={{ padding: "12px var(--oracle-pad, 24px) 0", display: "flex", flexDirection: "column", gap: 12, minWidth: 0, overflowX: "hidden" }} data-testid="oracle-phase27-strip">
        <OracleGlobalIntelGrid />
        <MarketPredictorHub />
      </div>

      {/* Phase 31 — ORACLE Personal Intelligence Layer (LOGIX-2 Personal Relationship Engine) */}
      <div style={{ padding: "12px var(--oracle-pad, 24px) 0", minWidth: 0, overflowX: "hidden" }} data-testid="oracle-phase31-personalization-wrap">
        <OraclePersonalizationEngine testid="oracle-personalization-engine" />
      </div>

      {/* Phase 32A — ORACLE Generation-3 Capability Absorption: Signal Engine + Event Intelligence + Agent Swarm */}
      <div style={{ padding: "12px var(--oracle-pad, 24px) 0", minWidth: 0, overflowX: "hidden", display: "flex", flexDirection: "column", gap: 14 }} data-testid="oracle-phase32a-wrap">
        <SignalCommandCenter testid="oracle-phase32-signal-command" />
        <EventIntelligenceCenter testid="oracle-phase32-event-intel" />
        <AgentSwarmConsole testid="oracle-phase32-swarm-console" />
      </div>

      {/* Phase 30E — Animated Global Markets Live map + Sector Heatmap + Daily Insight Cards */}
      <Phase30AnimatedStrip />

      {/* Phase 33 — Predictive Intelligence + Funding + Live Feeds (additive only).
          Embeds quick summary surfaces on the home dashboard. Full /oracle/predictive
          and /oracle/funding command routes are dedicated pages. */}
      <div
        style={{ padding: "12px var(--oracle-pad, 24px) 0", display: "flex", flexDirection: "column", gap: 12, minWidth: 0, overflowX: "hidden" }}
        data-testid="oracle-phase33-strip"
      >
        <SignalPulse testid="oracle-phase33-signal-pulse" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          <OraclePredictiveSummary testid="oracle-phase33-predictive-summary" />
          <AgentSwarmActivityPanel testid="oracle-phase33-swarm" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          <OpportunityGraph testid="oracle-phase33-opp-graph" />
          <GlobalIntelFeed testid="oracle-phase33-global-feed" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          <GrantDiscoveryFeed testid="oracle-phase33-grant-feed" />
          <HealthcareOpportunityRadar testid="oracle-phase33-healthcare-radar" />
          <StartupRadar testid="oracle-phase33-startup-radar" />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="oracle-phase33-cta-row">
          <RouterLink to="/oracle/predictive" data-testid="oracle-phase33-cta-predictive"
            style={{ color: "#0b0f14", background: "linear-gradient(180deg,#fde68a 0%,#c5a059 100%)", padding: "10px 16px", borderRadius: 12, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontWeight: 800, letterSpacing: "0.10em", fontSize: 13 }}>
            🟨 OPEN PREDICTIVE COMMAND™ →
          </RouterLink>
          <RouterLink to="/oracle/funding" data-testid="oracle-phase33-cta-funding"
            style={{ color: "#fde68a", background: "transparent", padding: "10px 16px", borderRadius: 12, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontWeight: 800, letterSpacing: "0.10em", fontSize: 13, border: "1px solid rgba(197,160,89,0.45)" }}>
            💰 OPEN FUNDING COMMAND™ →
          </RouterLink>
        </div>
      </div>

      {/* Phase 23 — compact privacy + compliance reassurance line */}
      <div className="oracle-phi-row" data-testid="oracle-phi-row">
        <span className="oracle-phi-row__chip">No PHI collection · Audit-ready</span>
        <span className="oracle-phi-row__copy">
          AI-powered intelligence across stocks, crypto, forex / OANDA, options, commodities, sports intelligence,
          healthcare investments, biotech / FDA catalysts, startups, grants, and global macro signals. Educational
          / research only — not financial advice.
        </span>
      </div>

      {/* Phase 26 — Mode-aware compliance banners (Sports / Healthcare) */}
      {activeMode === "sports" && (
        <div className="oracle-compliance-banner oracle-compliance-banner--sports" role="alert" data-testid="oracle-sports-21plus">
          <span className="oracle-compliance-banner__chip" aria-hidden="true">21+</span>
          <span className="oracle-compliance-banner__copy">
            <strong>Responsible-Gaming Notice.</strong> Sports intelligence is research output only —
            21+ where legal. Never chase losses. Set deposit / loss limits, take cool-off breaks, and
            use self-exclusion when needed. Expected value is shown honestly with bankroll risk;
            ORACLE does not promise guaranteed picks and never places wagers autonomously.
          </span>
        </div>
      )}
      {/* Phase 26E — Sports Responsible-Gaming Gate (additive; default-blocked) */}
      {activeMode === "sports" && (
        <div style={{ padding: "0 var(--oracle-pad, 24px)" }}>
          <SportsResponsibleGamingGate />
        </div>
      )}
      {(activeMode === "catalyst" || activeMode === "swing") && (
        <div className="oracle-compliance-banner oracle-compliance-banner--mnpi" role="note" data-testid="oracle-mnpi-banner">
          <span className="oracle-compliance-banner__chip" aria-hidden="true">PUBLIC ONLY</span>
          <span className="oracle-compliance-banner__copy">
            <strong>Healthcare / Biotech intelligence uses public information only.</strong> FDA calendar,
            clinical-trial public updates, public Form 4 insider filings, healthcare ETFs, and public
            pharma earnings. <b>Material non-public information (MNPI) is never accepted</b>, never
            ingested, and would be rejected by the engine.
          </span>
        </div>
      )}

      {/* Mode tabs (+ Report Center tab) */}
      <div className="oracle-tabs-wrap">
        <div className="oracle-mode-tabs" role="tablist" aria-label="ORACLE tabs">
          {AGENT_MODES.map((m) => {
            const active = !isReportTab && m.id === activeMode;
            return (
              <button
                key={m.id}
                data-testid={ORACLE_TEST_IDS.modeTab(m.id)}
                role="tab"
                aria-selected={active}
                className={`oracle-mode-tab ${active ? "is-active" : ""}`}
                onClick={() => setActiveTab(m.id)}
                type="button"
              >
                <span className="oracle-mode-tab__icon" aria-hidden="true">{m.icon}</span>
                <span className="oracle-mode-tab__label">
                  <span className="oracle-mode-tab__title">{m.short}</span>
                  <span className="oracle-mode-tab__sub">{m.tag}</span>
                </span>
              </button>
            );
          })}
          <button
            key={REPORT_TAB_ID}
            data-testid="oracle-mode-tab-report-center"
            role="tab"
            aria-selected={isReportTab}
            className={`oracle-mode-tab oracle-mode-tab--report ${isReportTab ? "is-active" : ""}`}
            onClick={() => setActiveTab(REPORT_TAB_ID)}
            type="button"
          >
            <span className="oracle-mode-tab__icon" aria-hidden="true">📊</span>
            <span className="oracle-mode-tab__label">
              <span className="oracle-mode-tab__title">Report Center</span>
              <span className="oracle-mode-tab__sub">Audit · Export · Status</span>
            </span>
          </button>
          <button
            key={PLATFORM_REPORT_TAB_ID}
            data-testid="oracle-mode-tab-platform-report"
            role="tab"
            aria-selected={isPlatformReportTab}
            className={`oracle-mode-tab oracle-mode-tab--report ${isPlatformReportTab ? "is-active" : ""}`}
            onClick={() => setActiveTab(PLATFORM_REPORT_TAB_ID)}
            type="button"
          >
            <span className="oracle-mode-tab__icon" aria-hidden="true">📋</span>
            <span className="oracle-mode-tab__label">
              <span className="oracle-mode-tab__title">Platform Report</span>
              <span className="oracle-mode-tab__sub">LOGIX-2™ · GIOS™ Overview</span>
            </span>
          </button>
        </div>
      </div>

      {isReportTab ? (
        <LockedFeature featureKey="pdf_dossiers">
          <ReportCenter
          userId={userId}
          currentSessionId={currentSessionId}
          activeMode={activeMode}
          metadata={currentMeta}
          watchlist={watchlist}
          adminStatus={adminStatus}
          liveStatus={liveStatus}
          exportLoading={exportLoading}
          onExportPdf={onExportPdf}
          onClearSession={onClearSession}
          onRefreshFeed={onRefreshFeed}
          onAddToWatchlist={onAddToWatchlist}
        />
        </LockedFeature>
      ) : isPlatformReportTab ? (
        <PlatformReport />
      ) : (
        <>
          {/* Mode head strip */}
          <div className="oracle-modehead">
            <div className="oracle-modehead__icon" style={{ color: activeModeMeta.accent }}>
              {activeModeMeta.icon}
            </div>
            <div className="oracle-modehead__txt">
              <div className="oracle-modehead__title">{activeModeMeta.label}</div>
              <div className="oracle-modehead__blurb">{activeModeMeta.blurb}</div>
            </div>

            {/* Inline enterprise actions */}
            <div className="oracle-modehead__actions">
              <button
                type="button"
                className="oracle-btn oracle-btn--ghost"
                onClick={onAddToWatchlist}
                disabled={loading}
                aria-label="Add the current ticker or event to your watchlist"
                data-testid="oracle-watchlist-add"
                title={messages.length === 0
                  ? "Add a ticker or event to your watchlist (you will be prompted)"
                  : "Add detected asset/event to watchlist"}
              >
                ＋ Watch
              </button>
              <button
                type="button"
                className="oracle-btn oracle-btn--ghost"
                onClick={onExportPdf}
                disabled={exportLoading}
                aria-label="Export the current ORACLE session as a branded PDF dossier"
                data-testid="oracle-export-pdf"
                title={!currentSessionId || messages.length === 0
                  ? "Run an ORACLE analysis first, then export"
                  : "Export branded PDF dossier"}
              >
                {exportLoading ? "Generating…" : "⬇ Export PDF"}
              </button>
              <button
                type="button"
                className="oracle-btn oracle-btn--ghost"
                data-testid={ORACLE_TEST_IDS.clear}
                onClick={handleClear}
                disabled={loading}
                aria-label="Clear ORACLE workspace for this mode"
                title="Clear ORACLE workspace for this mode"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Metadata strip (after first reply) */}
          {currentMeta?.metadata && (
            <div className="oracle-meta-strip" data-testid="oracle-meta-strip">
              <span className="meta-pill">
                Verdict: <b>{currentMeta.metadata.verdict || "—"}</b>
              </span>
              <span className="meta-pill">
                Confidence:
                <span className="meta-meter" aria-label="confidence meter">
                  <span
                    className="meta-meter__fill meta-meter__fill--gold"
                    style={{ width: `${Math.max(0, Math.min(10, currentMeta.metadata.confidence_score || 0)) * 10}%` }}
                  />
                </span>
                <b>{currentMeta.metadata.confidence_score ?? 0}/10</b>
              </span>
              <span className={`meta-pill meta-pill--risk meta-pill--risk-${(currentMeta.metadata.risk_level || "moderate").toLowerCase()}`}>
                Risk: <b>{currentMeta.metadata.risk_level || "—"}</b>
              </span>
              <span className="meta-pill">
                Horizon: <b>{currentMeta.metadata.time_horizon || "—"}</b>
              </span>
              <span className={`meta-pill ${dataFreshness === "live" ? "is-live" : ""}`}>
                Data: <b>{(dataFreshness || "public").toUpperCase()}</b>
              </span>
              {hitlRequired && <span className="meta-pill meta-pill--hitl">HITL REVIEW</span>}
            </div>
          )}

          <QuickPicks mode={activeMode} onPick={handlePick} disabled={loading} />

          {activeMode === "retire" && (
            <RetirementCalculator onQuery={(p) => sendMessage(p)} disabled={loading} />
          )}

          {activeMode === "oanda" && (
            <div className="oracle-oanda-modes" data-testid="oanda-mode-wrap">
              <div className="fx-mode-toggle" role="tablist" aria-label="OANDA experience mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!oandaProTrader}
                  className={`fx-mode-toggle__btn ${!oandaProTrader ? "is-active" : ""}`}
                  onClick={() => toggleProTrader(false)}
                  data-testid="oanda-mode-standard"
                >STANDARD</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={oandaProTrader}
                  className={`fx-mode-toggle__btn ${oandaProTrader ? "is-active" : ""}`}
                  onClick={() => toggleProTrader(true)}
                  data-testid="oanda-mode-pro"
                  title="Full OANDA-style trading terminal with LOGIX-2 intelligence"
                >PRO TRADER</button>
                <span className="fx-mode-toggle__hint" aria-hidden="true">
                  {oandaProTrader ? "Bloomberg-grade · research only" : "Beginner-friendly pair grid"}
                </span>
              </div>
              {oandaProTrader
                ? <OandaFxTradingScreen embedded />
                : <OandaPanel onPickPair={handlePick} loading={loading} disabled={loading} />
              }
            </div>
          )}

          <ChatPanel messages={messages} loading={loading} mode={activeMode} error={error} />

          <div className="oracle-composer">
            <textarea
              data-testid={ORACLE_TEST_IDS.input}
              className="oracle-composer__input"
              placeholder={`Ask the ${activeModeMeta.short.toUpperCase()} ORACLE… (Enter to send, Shift+Enter for newline)`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              disabled={loading}
            />
            <button
              type="button"
              data-testid={ORACLE_TEST_IDS.sendBtn}
              className="oracle-btn oracle-btn--gold"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
            >
              {loading ? "Thinking…" : "Ask ORACLE"}
            </button>
          </div>
        </>
      )}

      <DisclaimerFooter />

      {/* Phase 26C — Persistent Compliance Lock™ footer (mandated copy) */}
      <ComplianceLock variant="footer" testid="oracle-compliance-lock-footer" />

      {/* Phase 23 — LOGIX-2™ dock is gated behind public member auth.
          Guests + signed-out users see a discreet CTA pointing to /access. */}
      {memberAuth?.isAuthenticated ? (
        // Phase 30G — Animated voice dock wraps the existing Logix2Dock; greets
        // the user by display name once per session. Existing dock is preserved.
        <Logix2VoiceDock
          displayName={memberAuth?.user?.name || memberAuth?.user?.email}
          greetOnMount={false}
          testid="logix2-voice-dock"
        />
      ) : (
        <RouterLink
          to="/access"
          className="logix2-locked-cta"
          data-testid="logix2-locked-cta"
          title="Sign in to unlock LOGIX-2™ voice + intelligence dock"
        >
          <span className="logix2-locked-cta__glyph" aria-hidden="true">
            <img
              src="/brand/logix2-mascot.jpg"
              alt=""
              className="logix2-locked-cta__mascot"
              loading="lazy"
              decoding="async"
              data-testid="logix2-locked-cta-mascot"
            />
          </span>
          <span className="logix2-locked-cta__label">
            <strong>LOGIX-2™</strong>
            <em>{memberAuth?.isGuest ? "Sign in to unlock" : "Sign in for voice + intelligence"}</em>
          </span>
        </RouterLink>
      )}

      {/* Global Intelligence Chyron™ — broadcast ticker (Phase 18). Z-index
          sits BELOW the LOGIX-2 dock so it never occludes the dock's FAB. */}
      <GlobalIntelligenceChyron variant="public" />

      {/* Phase 30E — Premium animated CareLogix ticker, now personalized per user (Phase 31). */}
      <PersonalizedChyronTicker testid="oracle-phase31-chyron" sticky={false} height={30} />
    </div>
  );
}

function MembershipHeaderBadge() {
  const { tier, tierLabel, tiers, setDemoTier } = useMembership();
  const [open, setOpen] = React.useState(false);
  return (
    <span className="oracle-tier-badge" data-testid="oracle-tier-badge">
      <button
        type="button"
        className="oracle-tier-badge__btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="oracle-tier-badge-btn"
        title="Current membership tier (demo)"
      >
        <span className="oracle-tier-badge__dot" aria-hidden="true">★</span>
        <span className="oracle-tier-badge__lbl">{tierLabel || tier}</span>
      </button>
      {open && (
        <div className="oracle-tier-menu" role="menu" data-testid="oracle-tier-menu">
          <div className="oracle-tier-menu__head">Demo membership tier</div>
          {tiers.map((t) => (
            <button
              key={t}
              type="button"
              className={`oracle-tier-menu__item ${t === tier ? "is-active" : ""}`}
              onClick={() => { setDemoTier(t); setOpen(false); }}
              data-testid={`oracle-tier-menu-${t}`}
            >
              {t.toUpperCase()}
            </button>
          ))}
          <div className="oracle-tier-menu__foot">No payment processor wired</div>
        </div>
      )}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Phase 30E — Animated strip rendered between Phase 27 grid and the legacy
// content. Strictly additive. Honors the AnimationSettingsContext so AdminOne
// (or the operator's local prefs) can disable any individual piece.
// ---------------------------------------------------------------------------
function Phase30AnimatedStrip() {
  const { prefs, enabled } = useAnimationSettings();
  if (!prefs) return null;
  // When the master switch is off we still render static fallbacks so the
  // structural cards remain present (no removal of content). The motion
  // primitives inside each component already respect reduced motion.
  return (
    <section
      data-testid="oracle-phase30-animated-strip"
      style={{ padding: "12px var(--oracle-pad, 24px) 0", display: "flex", flexDirection: "column", gap: 12, minWidth: 0, overflowX: "hidden" }}
      aria-label="Animated market intelligence panels"
    >
      {prefs.globalMap         && <GlobalMarketMap   testid="oracle-phase30-global-map" />}
      {prefs.sectorHeatmap     && <SectorHeatmap     testid="oracle-phase30-sector-heatmap" />}
      {prefs.dailyInsightCards && <DailyInsightCards testid="oracle-phase30-daily-insight-cards" />}
      {!enabled && (
        <span
          style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.06em" }}
          data-testid="oracle-phase30-motion-paused-note"
        >
          ⏸ Animation paused (reduced motion / operator setting). All content remains accessible.
        </span>
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Phase 31 — Personalization badge in the top-right region of the home page,
// plus a UserPreferenceSetup modal that the badge can open. Strictly additive.
// Renders nothing for guests (the badge already short-circuits when not authed).
// ---------------------------------------------------------------------------
function Phase31BadgeAndPrefs() {
  const { isAuthed, profile } = usePersonalization();
  const [openPrefs, setOpenPrefs] = useState(false);
  return (
    <>
      <div
        style={{
          padding: "8px var(--oracle-pad, 24px) 0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
        data-testid="oracle-phase31-badge-row"
      >
        {isAuthed && (
          <OracleUserProfileBadge onOpenSettings={() => setOpenPrefs(true)} />
        )}
        {isAuthed && (
          <button
            type="button"
            onClick={() => setOpenPrefs(true)}
            data-testid="oracle-phase31-open-prefs"
            style={{
              appearance: "none",
              background: "transparent",
              border: "1px solid rgba(197,160,89,0.45)",
              color: "#c5a059",
              padding: "5px 11px",
              borderRadius: 999,
              fontFamily: "'Space Mono', monospace",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            ⚙ Personalize
          </button>
        )}
      </div>
      {isAuthed && (
        <UserPreferenceSetup
          asModal
          open={openPrefs}
          onClose={() => setOpenPrefs(false)}
          testid="oracle-phase31-prefs-modal"
        />
      )}
      {/* Reference unused-symbol guard to satisfy linters when other pieces are conditional */}
      {false && profile && null}
    </>
  );
}
